import { createContext, useContext, useState, useCallback, ReactNode } from 'react'
import AlertDialog from 'src/components/shared/ui/AlertDialog/AlertDialog'
import { AlertType } from 'src/components/shared/ui/AlertDialog/enums'

interface AlertProps {
  message: string | JSX.Element
  type: AlertType
  action?: () => void | Promise<void>
  closeAction?: () => void
}

interface Alert extends AlertProps {
  open: boolean
}

type AlertReturnedObject = {
  addAlert: (alert: AlertProps) => void
  closeAlert: () => void
}

const AlertContext = createContext({} as AlertReturnedObject)

export function AlertContextProvider({ children }: { children: ReactNode }): JSX.Element {
  const [{ message, type, action, closeAction, open }, setAlert] = useState<Alert>({
    open: false
  } as Alert)

  const addAlert = useCallback((alert: AlertProps) => {
    setAlert({ ...alert, open: true })
  }, [])

  function closeAlert() {
    if (closeAction) {
      closeAction()
    }
    setAlert({ message, type, action, open: false })
  }

  function onAcceptAlert() {
    if (action) {
      action()
    }
    closeAlert()
  }

  return (
    <AlertContext.Provider value={{ addAlert, closeAlert }}>
      {children}
      {type && (
        <AlertDialog
          show={open}
          type={type}
          message={message}
          close={() => closeAlert()}
          accept={onAcceptAlert}
          cancel={() => closeAlert()}
        />
      )}
    </AlertContext.Provider>
  )
}

export function useAlertContext(): AlertReturnedObject {
  return useContext(AlertContext)
}
